import { z } from 'zod';
import { FinanceInvoiceStatusSchema, FinanceInvoiceStoredStatusSchema, InvoiceSchema, convertEurToChf, } from './admin-finance';
import { ClientLedgerRowSchema } from './client-finance';
import { TopUpRequestSchema } from './deposit-topup';
import { FxConversionSchema, LedgerMethodSchema, PaymentCurrencySchema, PaymentOriginalSchema, } from './payment';
/**
 * Admin «Фінанси» tab of a client — ONE way in for money a client paid
 * (task `2026-09-29-client-finances-rethink`).
 *
 * `POST /api/admin/users/:userId/payments` records one payment and allocates it,
 * in one transaction, to the client's deposit and/or their invoices.
 * `GET /api/admin/users/:userId/finance` is the tab's single read model.
 *
 * Every figure suffixed `Chf` is CHF. `amount` is in the payment's `currency`;
 * a non-CHF payment is converted at the active pricing rule set's rate, exactly
 * as invoices are ({@link toChf} → {@link convertEurToChf}).
 */
/** Error codes the client-payment endpoints answer. Stable wire strings. */
export const CLIENT_PAYMENT_ERRORS = {
    allocationMismatch: 'ALLOCATION_MISMATCH',
    invoiceOverpaid: 'INVOICE_OVERPAID',
    invoiceNotPayable: 'INVOICE_NOT_PAYABLE',
    invoiceNotFound: 'INVOICE_NOT_FOUND',
    paymentsDisabled: 'PAYMENTS_DISABLED',
    receiptRequired: 'RECEIPT_REQUIRED',
    fxRateUnavailable: 'FX_RATE_UNAVAILABLE',
    paymentIdConflict: 'PAYMENT_ID_CONFLICT',
    depositInsufficient: 'DEPOSIT_INSUFFICIENT',
    legacyRetired: 'LEGACY_PAYMENTS_RETIRED',
};
/** Whole cents, `Math.round` — the same rounding the invoice helpers use. */
function toCents(value) {
    return Math.round(value * 100);
}
/**
 * Where one part of a payment goes. `invoice` is accepted on the wire; the
 * server refuses it with `PAYMENTS_DISABLED` while the legal gate is off.
 */
export const ClientPaymentAllocationSchema = z.discriminatedUnion('to', [
    z.object({ to: z.literal('deposit'), amountChf: z.number().finite().positive() }).strict(),
    z
        .object({
        to: z.literal('invoice'),
        invoiceNo: z.string().trim().min(1).max(32),
        amountChf: z.number().finite().positive(),
    })
        .strict(),
]);
/**
 * True when the allocations add up to `amountChf`, compared in whole cents
 * (each part rounded to the cent first). The ONE Σ rule — the server applies it
 * after conversion, the admin dialog before submit.
 */
export function allocationsMatch(allocations, amountChf) {
    const sum = allocations.reduce((cents, allocation) => cents + toCents(allocation.amountChf), 0);
    return sum === toCents(amountChf);
}
/**
 * Body of `POST /api/admin/users/:userId/payments`.
 *
 * - `paymentId` is the client-generated idempotency key: a replay returns the
 *   recorded result and writes nothing.
 * - Cash must carry a `reference` (the cash-desk receipt no.) → `RECEIPT_REQUIRED`.
 * - At most one deposit part, and no invoice twice.
 * - For CHF the Σ check runs here (`ALLOCATION_MISMATCH`); for another currency
 *   it runs server-side after conversion, with the same {@link allocationsMatch}.
 */
export const ClientPaymentRequestSchema = z
    .object({
    paymentId: z.string().uuid(),
    amount: z.number().finite().positive(),
    currency: PaymentCurrencySchema,
    receivedAt: z.coerce.date(),
    method: LedgerMethodSchema,
    /** Payment / receipt number; required for cash. */
    reference: z.string().trim().min(1).max(64).optional(),
    note: z.string().trim().max(500).optional(),
    allocations: z.array(ClientPaymentAllocationSchema).min(1).max(10),
})
    .strict()
    .superRefine((payment, ctx) => {
    if (payment.method === 'cash' && payment.reference === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['reference'],
            message: CLIENT_PAYMENT_ERRORS.receiptRequired,
        });
    }
    const depositParts = payment.allocations.filter((allocation) => allocation.to === 'deposit');
    if (depositParts.length > 1) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['allocations'],
            message: 'at most one deposit allocation',
        });
    }
    const invoiceNos = payment.allocations.flatMap((allocation) => allocation.to === 'invoice' ? [allocation.invoiceNo] : []);
    if (new Set(invoiceNos).size !== invoiceNos.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['allocations'],
            message: 'an invoice may be allocated only once',
        });
    }
    if (payment.currency === 'CHF' && !allocationsMatch(payment.allocations, payment.amount)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['allocations'],
            message: CLIENT_PAYMENT_ERRORS.allocationMismatch,
        });
    }
});
/**
 * A payment amount in CHF, and how it got there.
 *
 * - CHF → itself, cent-rounded, no fx record;
 * - EUR → {@link convertEurToChf} (the invoice helper — same rate, same rounding);
 * - anything else (USD, UAH) → `null`: the rule set has no CHF rate for it, and
 *   the caller answers `FX_RATE_UNAVAILABLE`. So does an unusable EUR rate.
 */
export function toChf(amount, currency, rules) {
    if (currency === 'CHF') {
        if (!Number.isFinite(amount))
            return null;
        return { amountChf: toCents(amount) / 100, fx: null };
    }
    if (currency === 'EUR') {
        const converted = convertEurToChf(amount, rules.chfToEurRate, rules.ratesVersion);
        return converted ? { amountChf: converted.valueChf, fx: converted.fx } : null;
    }
    return null;
}
/** One allocation as it was recorded. */
export const ClientPaymentResultAllocationSchema = z.object({
    to: z.enum(['deposit', 'invoice']),
    invoiceNo: z.string().optional(),
    amountChf: z.number().finite().positive(),
    /** The invoice's stored status after this payment (invoice parts only). */
    invoiceStatus: FinanceInvoiceStoredStatusSchema.optional(),
});
/** Response of `POST /api/admin/users/:userId/payments` (first write and replay alike). */
export const ClientPaymentResultSchema = z.object({
    paymentId: z.string(),
    /** True when this `paymentId` was already recorded and nothing was written now. */
    replayed: z.boolean(),
    amount: z.number().finite().positive(),
    currency: PaymentCurrencySchema,
    amountChf: z.number().finite().positive(),
    fx: FxConversionSchema.nullable(),
    allocations: z.array(ClientPaymentResultAllocationSchema),
    /** The client's deposit after the payment. */
    depositAfterChf: z.number().finite(),
});
/** Reason an operator must give for a deposit «Коригування». */
// Optional since 2026-10-25 (owner: «примітки не обовʼязкові»): up to 500
// characters, may be empty. Older audit rows (10+ characters) still parse.
export const DepositAdjustmentReasonSchema = z.string().trim().max(500);
/* ───────────────────── GET /api/admin/users/:userId/finance ───────────────── */
/** Tab summary tiles. `ceilingChf` is null in trusted mode (no CHF ceiling). */
export const AdminClientFinanceSummarySchema = z.object({
    depositChf: z.number().finite(),
    invoicesOwedChf: z.number().finite().nonnegative(),
    overdueChf: z.number().finite().nonnegative(),
    ceilingChf: z.number().finite().nullable(),
});
/** One of the client's invoices, with what the tab renders beside it. */
export const AdminClientInvoiceSchema = InvoiceSchema.extend({
    /** {@link deriveInvoiceStatus} at read time. */
    displayStatus: FinanceInvoiceStatusSchema,
    /** {@link invoiceOutstandingChf}. */
    outstandingChf: z.number().finite().nonnegative(),
    lotTitle: z.string().nullable(),
});
/** A journal row: the client's ledger row plus who wrote it and why. */
export const AdminClientJournalRowSchema = ClientLedgerRowSchema.extend({
    actorId: z.string().nullable(),
    /**
     * The actor's display name (`personDisplayName` of the admin's user
     * document), resolved server-side. Null for a system actor (a script name
     * such as `migrate-deposit-multiplier`) or an id with no user — the UI then
     * shows `actorId`. Optional so an older response still parses.
     */
    actorName: z.string().nullable().optional(),
    note: z.string().nullable(),
});
/**
 * One money movement as the operator reads it: a payment with its allocations
 * beneath (grouped by `paymentId`), or a single ungrouped row (`paymentId` null
 * — legacy rows, adjustments, refunds, prepayments).
 */
export const AdminClientJournalEntrySchema = z.object({
    paymentId: z.string().nullable(),
    at: z.coerce.date(),
    method: LedgerMethodSchema.nullable(),
    reference: z.string().nullable(),
    /** Signed CHF total of the rows. */
    totalChf: z.number().finite(),
    rows: z.array(AdminClientJournalRowSchema),
    /**
     * When the payment was received (operator's date) — `at` equals it when
     * present. Null/absent for rows not written by the payment endpoint.
     */
    receivedAt: z.coerce.date().nullable().optional(),
    /** The whole payment in its original currency (e.g. EUR 1 000); null/absent otherwise. */
    original: PaymentOriginalSchema.nullable().optional(),
});
/** `GET /api/admin/users/:userId/finance` — the admin client «Фінанси» tab. */
export const AdminClientFinanceSchema = z.object({
    summary: AdminClientFinanceSummarySchema,
    /** `PAYMENTS_ENABLED`: whether an invoice allocation is accepted. */
    paymentsEnabled: z.boolean(),
    /** Active rule-set rate for the ≈ CHF preview; null when none is usable. */
    fx: z
        .object({
        chfToEurRate: z.number().finite().positive(),
        ratesVersion: z.string(),
    })
        .nullable(),
    invoices: z.array(AdminClientInvoiceSchema),
    /** Newest first. */
    journal: z.array(AdminClientJournalEntrySchema),
    openTopUpRequests: z.array(TopUpRequestSchema),
    /** Legacy `clientPayments` rows, read-only. */
    archive: z.object({ count: z.number().int().nonnegative() }),
});
//# sourceMappingURL=client-payment.js.map